import React, { Component, useRef } from 'react';
import PropTypes from 'prop-types';
import { Tooltip, Select } from 'antd';
import translate from 'counterpart';
import { path, prop, contains, isEmpty } from 'ramda';
import cx from 'classnames';

const { Option } = Select;

import withFieldWrapper from 'components/hocs/withFieldWrapper';
import { Message } from 'components/common/form/ErrorHTML';
import withLoad from 'components/hocs/withLoad';
import CustomIcon from 'components/common/CustomIcon';

const isSelected = (value, values) => contains(value, values);
const onSearchHandler = (input, option) => (option.children ? option.children.innerText || option.children || '' : '').toString().toLowerCase().indexOf((input || '').toLowerCase()) >= 0;

const SelectError = ({ error, refresh }) => error ? (
    <div className="error-indicator" onClick={() => refresh()}>
        <CustomIcon type="info-warning" />
    </div>
) : null;

const getCurrentValue = (value, stringifyValue) => (stringifyValue && value ? value.toString() : value) || undefined;

export const SelectBase = (selectProps) => {
    const selectContainer = useRef(null);

    const { options, disabled, pending, loading, error, refresh, value, valuePath, namePath, nameRender, stringifyValue, multi, multiMax, showSearch = true, placeholderDefault, ...props } = selectProps;
    const getValue = (item) => getCurrentValue(item[valuePath], stringifyValue);
    const maxSelected = multi && Array.isArray(value) && multiMax && (value.length >= multiMax);
    let hasValueOption = false;

    return (
        <div className={cx(selectProps.className, 'antd-select-container')} ref={selectContainer}>
            <SelectError error={error} refresh={refresh} />
            <Select
                {...props}
                showSearch={showSearch}
                key={props.value || 'empty'}
                value={value}
                defaultValue={pending || error || !value ? undefined : stringifyValue ? value.toString() : value }
                className={cx({ 'load-error': !!error })}
                disabled={pending || disabled || !!error}
                filterOption={selectProps.onSearch ? false : onSearchHandler}
                loading={loading || pending}
                closeOnSelect={true}
                showArrow={!error}
                getPopupContainer={() => selectContainer.current}
            >
                { options.map((option, index) => {
                    let optionLabel = nameRender ? nameRender(option) : option[namePath];
                    let optionValue = getValue(option);
                    hasValueOption = hasValueOption || value === optionValue;

                    return (
                        <Option
                            key={optionValue ? `${optionValue}-${index}` : ''}
                            value={optionValue}
                            data={option}
                            title={optionLabel}
                            disabled={ maxSelected && !isSelected(optionValue, value) ? true : option.disabled}
                        >
                            { optionLabel }
                        </Option>
                    );
                })}
                { !!placeholderDefault && !hasValueOption && !isEmpty(value) && (
                    <Option
                        key={'nooption'}
                        value={value}
                        disabled={true}
                    >
                        {props.placeholder || ''}
                    </Option>
                )}
            </Select>
        </div>
    );
}

class SelectComponent extends Component {
    static propTypes = {
        input: PropTypes.object,
        options: PropTypes.array,
        placeholderDefault: PropTypes.bool,
        placeholder: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.element
        ]),
        namePath: PropTypes.string,
        valuePath: PropTypes.string,
        allowClear: PropTypes.bool,
        showSearch: PropTypes.bool,
        disabled: PropTypes.bool,
        multi: PropTypes.bool,
        multiMax: PropTypes.number,
        onChangeTransformer: PropTypes.func,
        valueTransformer: PropTypes.func,
        nameRender: PropTypes.func,
        stringifyValue: PropTypes.bool,
        dictionaryAction: PropTypes.object,
        onChange: PropTypes.func,
        loading: PropTypes.bool,
        fieldClassName: PropTypes.string,
    };

    static defaultProps = {
        namePath: 'name',
        valuePath: 'id',
        options: [],
        stringifyValue: true,
        allowClear: false,
        placeholder: '',
        multi: false,
    };

    onChange = (value, options) => {
        const { onChange, onChangeTransformer } = this.props;
        onChange(onChangeTransformer ? onChangeTransformer(value, options) : value || null, options);
    }

    render() {
        const {
            options,
            namePath,
            valuePath,
            input: { value, name },
            placeholder,
            placeholderDefault,
            allowClear,
            showSearch,
            disabled,
            multi,
            valueTransformer,
            nameRender,
            stringifyValue,
            loading,
            multiMax,
            fieldClassName,
        } = this.props;

        const meta = path(['dictionaryAction', 'meta'], this.props);
        const refresh = path(['dictionaryAction', 'refresh'], this.props);
        const pending = prop('pending', meta);
        const error = prop('error', meta);
        const transformedValue = valueTransformer ? valueTransformer(value) : getCurrentValue(value, stringifyValue);

        return (
            <Tooltip title={error ? <Message error={error} /> : ''}>
                <SelectBase
                    name={name}
                    autoFocus={true}
                    multi={multi}
                    multiMax={multiMax}
                    mode={multi ? 'multiple' : undefined}
                    disabled={disabled}
                    options={options}
                    namePath={namePath}
                    valuePath={valuePath}
                    value={transformedValue || []}
                    onChange={this.onChange}
                    stringifyValue={stringifyValue}
                    placeholder={placeholder}
                    placeholderDefault={placeholderDefault}
                    allowClear={allowClear}
                    showSearch={showSearch}
                    pending={loading || pending}
                    error={error}
                    refresh={refresh}
                    closeOnSelect={!multi}
                    notFoundContent={translate('errors.notFound')}
                    nameRender={nameRender}
                    className={fieldClassName}
                />
            </Tooltip>
        );
    }
}

export const SelectLoadComponent = withFieldWrapper(withLoad(SelectComponent));

export default withFieldWrapper(SelectComponent);
